// src/pages/shop/ShopPage.jsx
//
// Step 1 of the journey: browse. Clicking a product opens /product/:id where
// the customer picks size/colour/quantity and adds to cart or buys now.

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useShopifyProducts,
  useShopifyCategories,
} from "../../hooks/useShopifyProducts";
import ShopNavbar from "../../components/shop/ShopNavbar";
import { T, F } from "../../config/shop";

const ITEMS_PER_PAGE = 36;

const isAvailable = (product) => {
  const price = parseFloat(product.price) || 0;
  return product.available !== undefined ? Boolean(product.available) : price > 0;
};

// ── Stars ───────────────────────────────────────────────────────────────────
function Stars({ rating, count, size = 12.5 }) {
  const r = typeof rating === "number" && !isNaN(rating) ? rating : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          style={{
            fontSize: size,
            color: n <= Math.round(r) ? T.black : "rgba(0,0,0,0.16)",
            lineHeight: 1,
          }}
        >
          ★
        </span>
      ))}
      <span style={{ fontFamily: F.body, fontSize: 12, color: T.muted, marginLeft: 4 }}>
        ({count || 0})
      </span>
    </div>
  );
}

// ── Skeleton ────────────────────────────────────────────────────────────────
function SkeletonCard() {
  const shimmer = {
    background: "linear-gradient(90deg,#eeeeee 25%,#e2e2e2 50%,#eeeeee 75%)",
    backgroundSize: "200% 100%",
    animation: "km_shimmer 1.4s ease-in-out infinite",
  };
  return (
    <div>
      <div className="km-img" style={{ borderRadius: 16, ...shimmer }} />
      <div style={{ padding: "12px 0 4px" }}>
        <div style={{ height: 11, width: "90%", marginBottom: 8, ...shimmer }} />
        <div style={{ height: 10, width: "50%", marginBottom: 10, ...shimmer }} />
        <div style={{ height: 20, width: "45%", marginBottom: 10, ...shimmer }} />
      </div>
    </div>
  );
}

// ── Product card (flat, no box) ─────────────────────────────────────────────
function ProductCard({ product }) {
  const navigate = useNavigate();
  const [hov, setHov] = useState(false);

  const price = parseFloat(product.price) || 0;
  const available = isAvailable(product);
  const seller = product.vendor || product.seller_name || "Kaakazini Artisan";
  const title = product.title || product.name || "Product";

  const open = () => navigate(`/product/${product.id}`);

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter") open();
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ cursor: "pointer", display: "flex", flexDirection: "column", minWidth: 0 }}
    >
      <div
        className="km-img"
        style={{ position: "relative", overflow: "hidden", background: "#fafaf8", width: "100%", borderRadius: 16 }}
      >
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={title}
            loading="lazy"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              transform: hov ? "scale(1.035)" : "scale(1)",
              transition: "transform .3s ease",
            }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              color: T.muted,
            }}
          >
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
              <rect x="3" y="3" width="18" height="18" rx="1" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
            <span style={{ fontFamily: F.body, fontSize: 12 }}>No image</span>
          </div>
        )}

        {!available && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(255,255,255,0.82)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                background: T.black,
                color: T.white,
                fontFamily: F.body,
                fontSize: 12.5,
                fontWeight: 700,
                padding: "6px 16px",
                borderRadius: 2,
                letterSpacing: ".05em",
              }}
            >
              Sold out
            </span>
          </div>
        )}
      </div>

      <div style={{ padding: "12px 0 4px", flex: 1, display: "flex", flexDirection: "column" }}>
        <div
          style={{
            fontFamily: F.body,
            fontSize: 13.5,
            fontWeight: 500,
            color: T.ink,
            lineHeight: 1.35,
            marginBottom: 4,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            minHeight: "2.7em",
          }}
        >
          {title}
        </div>
        <div style={{ fontFamily: F.body, fontSize: 12, color: T.muted, marginBottom: 6 }}>by {seller}</div>
        <div style={{ marginBottom: 6 }}>
          <Stars rating={product.rating} count={product.review_count} />
        </div>
        <div style={{ fontFamily: F.body, fontSize: 18, fontWeight: 800, color: T.black, marginBottom: 4 }}>
          KSh {price.toLocaleString()}
        </div>
        <div style={{ fontFamily: F.body, fontSize: 11.5, color: T.muted, marginTop: 2 }}>View product →</div>
      </div>
    </div>
  );
}

// ── Sidebar ─────────────────────────────────────────────────────────────────
const PRICE_RANGES = [
  { label: "Under KSh 500", value: "0-500" },
  { label: "KSh 500 – 1,000", value: "500-1000" },
  { label: "KSh 1,000 – 5,000", value: "1000-5000" },
  { label: "KSh 5,000 – 10,000", value: "5000-10000" },
  { label: "Over KSh 10,000", value: "10000-999999" },
];

// Defined at module level (it used to be defined inside FilterSidebar, which
// re-created it on every render).
function FilterSection({ title, children }) {
  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{ fontFamily: F.body, fontSize: 13, fontWeight: 700, color: T.ink, marginBottom: 12 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function FilterOption({ type, name, checked, onChange, label }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 10, cursor: "pointer" }}>
      <input
        type={type}
        name={name}
        checked={checked}
        onChange={onChange}
        style={{ accentColor: T.black, cursor: "pointer", width: 15, height: 15 }}
      />
      <span style={{ fontFamily: F.body, fontSize: 13.5, color: checked ? T.ink : T.inkSoft, fontWeight: checked ? 700 : 400 }}>
        {label}
      </span>
    </label>
  );
}

function FilterSidebar({ categories, selectedCategory, onCategory, priceRange, onPrice, inStockOnly, onInStock }) {
  return (
    <div style={{ width: 230, flexShrink: 0, alignSelf: "flex-start", position: "sticky", top: 84 }}>
      <div
        style={{
          fontFamily: F.display,
          fontSize: 15,
          fontWeight: 800,
          color: T.ink,
          marginBottom: 20,
          paddingBottom: 10,
          borderBottom: `3px solid ${T.yellow}`,
        }}
      >
        Filters
      </div>

      <FilterSection title="Category">
        <FilterOption type="radio" name="category" checked={!selectedCategory} onChange={() => onCategory(null)} label="All categories" />
        {categories.map((cat) => (
          <FilterOption
            key={cat}
            type="radio"
            name="category"
            checked={selectedCategory === cat}
            onChange={() => onCategory(cat)}
            label={cat}
          />
        ))}
      </FilterSection>

      <FilterSection title="Price">
        <FilterOption type="radio" name="price" checked={!priceRange} onChange={() => onPrice(null)} label="Any price" />
        {PRICE_RANGES.map((p) => (
          <FilterOption
            key={p.value}
            type="radio"
            name="price"
            checked={priceRange === p.value}
            onChange={() => onPrice(p.value)}
            label={p.label}
          />
        ))}
      </FilterSection>

      <FilterSection title="Availability">
        <FilterOption type="checkbox" checked={inStockOnly} onChange={(e) => onInStock(e.target.checked)} label="In stock only" />
      </FilterSection>
    </div>
  );
}

// ── Category bar ────────────────────────────────────────────────────────────
function CategoryBar({ categories, selectedCategory, onCategory }) {
  return (
    <div style={{ background: T.white, borderBottom: `1px solid ${T.border}` }}>
      <div
        className="km-catbar"
        style={{ maxWidth: 1280, margin: "0 auto", padding: "0 16px", display: "flex", gap: 6, overflowX: "auto" }}
      >
        <button onClick={() => onCategory(null)} className={`km-cat-pill${!selectedCategory ? " active" : ""}`}>
          All
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => onCategory(selectedCategory === cat ? null : cat)}
            className={`km-cat-pill${selectedCategory === cat ? " active" : ""}`}
          >
            {cat}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Pagination (now reaches every page) ─────────────────────────────────────
function pageList(page, totalPages) {
  const wanted = new Set([1, totalPages, page - 1, page, page + 1]);
  const nums = [...wanted].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
  const out = [];
  nums.forEach((n, i) => {
    if (i > 0 && n - nums[i - 1] > 1) out.push(`gap-${n}`);
    out.push(n);
  });
  return out;
}

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;

  const go = (p) => {
    if (p >= 1 && p <= totalPages && p !== page) onChange(p);
  };
  const edge = (disabled) => ({
    padding: "9px 16px",
    border: `1px solid ${T.border}`,
    background: T.white,
    cursor: disabled ? "not-allowed" : "pointer",
    color: disabled ? T.faint : T.ink,
    fontFamily: F.body,
    fontSize: 13.5,
    borderRadius: 2,
  });

  return (
    <nav
      aria-label="Pagination"
      style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, padding: "36px 0 20px", flexWrap: "wrap" }}
    >
      <button onClick={() => go(page - 1)} disabled={page === 1} style={edge(page === 1)}>
        ‹ Previous
      </button>

      {pageList(page, totalPages).map((p) =>
        typeof p === "string" ? (
          <span key={p} style={{ fontFamily: F.body, fontSize: 13.5, color: T.muted }}>
            …
          </span>
        ) : (
          <button
            key={p}
            onClick={() => go(p)}
            aria-current={p === page ? "page" : undefined}
            style={{
              width: 40,
              height: 40,
              border: `1px solid ${T.black}`,
              background: p === page ? T.black : T.white,
              color: p === page ? T.yellow : T.ink,
              cursor: "pointer",
              fontFamily: F.body,
              fontSize: 13.5,
              fontWeight: p === page ? 700 : 400,
              borderRadius: 2,
            }}
          >
            {p}
          </button>
        )
      )}

      <button onClick={() => go(page + 1)} disabled={page === totalPages} style={edge(page === totalPages)}>
        Next ›
      </button>
    </nav>
  );
}

// ── Shop page ───────────────────────────────────────────────────────────────
const SORT_TABS = [
  { key: "popular", label: "Popular" },
  { key: "top_sale", label: "Top sale" },
  { key: "latest", label: "Latest" },
  { key: "price_asc", label: "Low to high" },
  { key: "price_desc", label: "High to low" },
];

export default function ShopPage() {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [search, setSearch] = useState("");
  const [inputVal, setInputVal] = useState("");
  const [sortBy, setSortBy] = useState("popular");
  const [priceRange, setPriceRange] = useState(null);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [page, setPage] = useState(1);

  const { products, loading, error } = useShopifyProducts(selectedCategory);
  const rawCategories = useShopifyCategories();
  const categories = Array.isArray(rawCategories) ? rawCategories : [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let min = 0;
    let max = Infinity;
    if (priceRange) [min, max] = priceRange.split("-").map(Number);

    const list = (products || []).filter((p) => {
      const title = (p.title || p.name || "").toLowerCase();
      const type = (p.product_type || "").toLowerCase();
      const price = parseFloat(p.price) || 0;

      if (q && !title.includes(q) && !type.includes(q)) return false;
      if (priceRange && (price < min || price > max)) return false;
      if (inStockOnly && !isAvailable(p)) return false;
      return true;
    });

    const num = (v) => parseFloat(v) || 0;
    list.sort((a, b) => {
      if (sortBy === "price_asc") return num(a.price) - num(b.price);
      if (sortBy === "price_desc") return num(b.price) - num(a.price);
      if (sortBy === "latest") return (Number(b.id) || 0) - (Number(a.id) || 0);
      if (sortBy === "top_sale") return (b.review_count || 0) - (a.review_count || 0);
      return 0;
    });
    return list;
  }, [products, search, priceRange, inStockOnly, sortBy]);

  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    setSearch(inputVal);
    setPage(1);
  };
  const handleCategory = (cat) => {
    setSelectedCategory(cat);
    setPage(1);
  };
  const handlePrice = (val) => {
    setPriceRange(val);
    setPage(1);
  };
  const handleInStock = (val) => {
    setInStockOnly(val);
    setPage(1);
  };
  const clearFilters = () => {
    setSearch("");
    setInputVal("");
    setSelectedCategory(null);
    setPriceRange(null);
    setInStockOnly(false);
    setPage(1);
  };

  const errorText = error ? String(error.message || error) : "";

  return (
    <>
      <style>{`
        @keyframes km_shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes km_fadein { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

        .km-img { height: 280px; border-radius: 16px; }
        .km-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 34px 20px; }
        .km-grid > * { animation: km_fadein .2s ease both; }

        .km-catbar::-webkit-scrollbar { display: none; }
        .km-cat-pill {
          flex-shrink: 0; padding: 12px 14px; font-family: 'Raleway', sans-serif; font-size: 13.5px; font-weight: 600;
          background: none; border: none; border-bottom: 2px solid transparent; color: rgba(0,0,0,0.55);
          cursor: pointer; white-space: nowrap; transition: all .1s;
        }
        .km-cat-pill:hover { color: #000; }
        .km-cat-pill.active { color: #000; border-bottom-color: ${T.yellow}; }

        .km-sort-tab {
          padding: 10px 16px; font-family: 'Raleway', sans-serif; font-size: 13.5px; font-weight: 500;
          background: none; border: none; border-bottom: 2px solid transparent; cursor: pointer;
          color: rgba(0,0,0,0.55); white-space: nowrap; transition: all .1s;
        }
        .km-sort-tab:hover { color: #000; }
        .km-sort-tab.active { font-weight: 700; color: #000; border-bottom-color: ${T.yellow}; }

        @media (max-width: 1200px) { .km-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
        @media (max-width: 900px)  { .km-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 768px)  {
          .km-sidebar { display: none !important; }
          .km-img { height: 240px !important; border-radius: 14px; }
          .km-grid { gap: 28px 14px; }
        }
        @media (max-width: 480px)  {
          .km-grid { gap: 24px 10px; }
          .km-img { height: 190px !important; }
        }
      `}</style>

      <div style={{ minHeight: "100vh", background: T.surface, fontFamily: F.body }}>
        <ShopNavbar search={{ value: inputVal, onChange: setInputVal, onSubmit: handleSearch }} />

        <CategoryBar categories={categories} selectedCategory={selectedCategory} onCategory={handleCategory} />

        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "28px 16px 60px", display: "flex", gap: 28, alignItems: "flex-start" }}>
          <div className="km-sidebar">
            <FilterSidebar
              categories={categories}
              selectedCategory={selectedCategory}
              onCategory={handleCategory}
              priceRange={priceRange}
              onPrice={handlePrice}
              inStockOnly={inStockOnly}
              onInStock={handleInStock}
            />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontFamily: F.display, fontSize: 22, fontWeight: 800, color: T.ink, margin: "0 0 4px" }}>
              Explore products
            </h1>

            {(selectedCategory || search) && (
              <div style={{ fontFamily: F.body, fontSize: 13, color: T.muted, marginBottom: 14 }}>
                {selectedCategory ? `In ${selectedCategory}` : `Results for "${search}"`}
              </div>
            )}

            {!loading && !error && (
              <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${T.borderSoft}`, marginBottom: 20, overflowX: "auto" }}>
                {SORT_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    className={`km-sort-tab${sortBy === tab.key ? " active" : ""}`}
                    onClick={() => {
                      setSortBy(tab.key);
                      setPage(1);
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
                <span style={{ marginLeft: "auto", fontFamily: F.body, fontSize: 12.5, color: T.muted, whiteSpace: "nowrap", flexShrink: 0 }}>
                  {totalCount.toLocaleString()} results
                </span>
              </div>
            )}

            {loading && (
              <div className="km-grid">
                {Array.from({ length: 12 }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            )}

            {error && (
              <div style={{ padding: "4rem 2rem", textAlign: "center" }}>
                <div style={{ fontFamily: F.body, fontSize: 16, fontWeight: 700, color: T.ink, marginBottom: 8 }}>
                  Products could not be loaded
                </div>
                <div style={{ fontFamily: F.body, fontSize: 13.5, color: T.muted, marginBottom: 18 }}>{errorText}</div>
                <button
                  onClick={() => window.location.reload()}
                  style={{ padding: "10px 22px", background: T.black, color: T.yellow, border: "none", fontFamily: F.body, fontSize: 13.5, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}
                >
                  Try again
                </button>
              </div>
            )}

            {!loading && !error && paginated.length === 0 && (
              <div style={{ padding: "4rem 2rem", textAlign: "center" }}>
                <div style={{ fontFamily: F.body, fontSize: 16, fontWeight: 700, color: T.ink, marginBottom: 8 }}>
                  No products found
                </div>
                <div style={{ fontFamily: F.body, fontSize: 13.5, color: T.muted, marginBottom: 18 }}>
                  {search ? `Nothing matches "${search}".` : "Nothing matches these filters."}
                </div>
                {(search || selectedCategory || priceRange || inStockOnly) && (
                  <button
                    onClick={clearFilters}
                    style={{ padding: "10px 22px", background: T.yellow, color: T.black, border: "none", fontFamily: F.body, fontSize: 13.5, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}

            {!loading && !error && paginated.length > 0 && (
              <div className="km-grid">
                {paginated.map((p) => (
                  <ProductCard key={String(p.id)} product={p} />
                ))}
              </div>
            )}

            <Pagination
              page={safePage}
              totalPages={totalPages}
              onChange={(p) => {
                setPage(p);
                window.scrollTo({ top: 0 });
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
